import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { en } from '@/locales/en';
import { fr } from '@/locales/fr';
import { yo } from '@/locales/yo';
import { es } from '@/locales/es';
import { ar } from '@/locales/ar';
import { hi } from '@/locales/hi';
import { pt } from '@/locales/pt';
import { sw } from '@/locales/sw';
import { zh } from '@/locales/zh';
// Newly added (machine-translated via Claude Sonnet 4.6, pending native-speaker QA).
import { ig } from '@/locales/ig';
import { ha } from '@/locales/ha';
import { am } from '@/locales/am';
import { zu } from '@/locales/zu';
import { sn } from '@/locales/sn';
import { ak } from '@/locales/ak';
import { bn } from '@/locales/bn';
import { ur } from '@/locales/ur';
import { tr } from '@/locales/tr';
import { ko } from '@/locales/ko';
import { ja } from '@/locales/ja';
import { it } from '@/locales/it';
import { de } from '@/locales/de';

const LanguageContext = createContext();

const translations = {
  en, es, fr, ar, hi, pt, sw, zh, yo,
  ig, ha, am, zu, sn, ak, bn, ur, tr, ko, ja, it, de,
};

// Regional grouping for the language picker. `region` keys map to
// translation keys under `settings.regions.*` so headings localise too.
// `starter: true` flags languages whose translations are machine-generated
// and awaiting native-speaker QA — surfaced as a subtle badge in the UI.
const languages = [
  // Global
  { code: 'en', name: 'English',     flag: '🇬🇧', rtl: false, region: 'global'   },
  { code: 'es', name: 'Español',     flag: '🇪🇸', rtl: false, region: 'european' },
  { code: 'fr', name: 'Français',    flag: '🇫🇷', rtl: false, region: 'european' },
  { code: 'pt', name: 'Português',   flag: '🇧🇷', rtl: false, region: 'european' },
  { code: 'it', name: 'Italiano',    flag: '🇮🇹', rtl: false, region: 'european', starter: true },
  { code: 'de', name: 'Deutsch',     flag: '🇩🇪', rtl: false, region: 'european', starter: true },
  { code: 'tr', name: 'Türkçe',      flag: '🇹🇷', rtl: false, region: 'european', starter: true },
  // Middle Eastern
  { code: 'ar', name: 'العربية',     flag: '🇸🇦', rtl: true,  region: 'middle_eastern' },
  { code: 'ur', name: 'اردو',         flag: '🇵🇰', rtl: true,  region: 'middle_eastern', starter: true },
  // Asian
  { code: 'hi', name: 'हिन्दी',         flag: '🇮🇳', rtl: false, region: 'asian' },
  { code: 'bn', name: 'বাংলা',         flag: '🇧🇩', rtl: false, region: 'asian', starter: true },
  { code: 'zh', name: '中文',         flag: '🇨🇳', rtl: false, region: 'asian' },
  { code: 'ja', name: '日本語',       flag: '🇯🇵', rtl: false, region: 'asian', starter: true },
  { code: 'ko', name: '한국어',       flag: '🇰🇷', rtl: false, region: 'asian', starter: true },
  // African
  { code: 'sw', name: 'Kiswahili',   flag: '🇰🇪', rtl: false, region: 'african' },
  { code: 'yo', name: 'Yorùbá',      flag: '🇳🇬', rtl: false, region: 'african' },
  { code: 'ig', name: 'Igbo',        flag: '🇳🇬', rtl: false, region: 'african', starter: true },
  { code: 'ha', name: 'Hausa',       flag: '🇳🇬', rtl: false, region: 'african', starter: true },
  { code: 'ak', name: 'Akan (Twi)',  flag: '🇬🇭', rtl: false, region: 'african', starter: true },
  { code: 'am', name: 'አማርኛ',         flag: '🇪🇹', rtl: false, region: 'african', starter: true },
  { code: 'zu', name: 'isiZulu',     flag: '🇿🇦', rtl: false, region: 'african', starter: true },
  { code: 'sn', name: 'chiShona',    flag: '🇿🇼', rtl: false, region: 'african', starter: true },
];

// Dev mode - warn about missing translations
const DEV_MODE = process.env.NODE_ENV === 'development';
const missingKeysCache = new Set();

// Expose missing-key report on window for the audit script + dev console.
// Usage: window.__i18nMissingKeys() → array of "lang:key" strings.
if (typeof window !== 'undefined') {
  window.__i18nMissingKeys = () => Array.from(missingKeysCache);
  window.__i18nResetMissingKeys = () => missingKeysCache.clear();
}

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    // Load saved language preference
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
      // Set document direction and lang attribute for RTL languages
      const lang = languages.find(l => l.code === savedLanguage);
      if (lang?.rtl) {
        document.documentElement.setAttribute('dir', 'rtl');
        document.documentElement.setAttribute('lang', savedLanguage);
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
        document.documentElement.setAttribute('lang', savedLanguage);
      }
    }
  }, []);

  const changeLanguage = (languageCode, showToast = true) => {
    if (translations[languageCode] && languageCode !== currentLanguage) {
      setIsChanging(true);
      
      // Get language info
      const newLang = languages.find(l => l.code === languageCode);
      const oldLang = languages.find(l => l.code === currentLanguage);
      
      // Update state and storage
      setCurrentLanguage(languageCode);
      localStorage.setItem('language', languageCode);
      
      // Update document direction and language
      if (newLang?.rtl) {
        document.documentElement.setAttribute('dir', 'rtl');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
      }
      document.documentElement.setAttribute('lang', languageCode);
      
      // Show success toast
      if (showToast) {
        const message = getSafeTranslation(languageCode, 'settings.languageUpdated') || 
                       'Language updated successfully';
        
        toast.success(message, {
          description: `${oldLang?.flag || ''} ${oldLang?.name || ''} to ${newLang?.flag || ''} ${newLang?.name || ''}`,
          duration: 3000,
        });
      }
      
      setIsChanging(false);
      
      // Force component re-render by triggering a small state change
      window.dispatchEvent(new Event('languagechange'));
    }
  };

  // Helper function to safely get translation without triggering warnings
  const getSafeTranslation = (lang, key) => {
    const keys = key.split('.');
    let value = translations[lang];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return null;
      }
    }
    
    return value;
  };

  // Robust translation function with strict fallback + interpolation.
  // Usage:
  //   t('common.save')                  → translated string
  //   t('key', 'default')               → translated, or 'default' if missing
  //   t('greet', { name: 'Esther' })    → interpolates {{name}}
  //   t('greet', { name: 'X' }, 'def')  → interpolation + fallback
  const t = (key, paramsOrFallback = null, maybeFallback = null) => {
    // Disambiguate second/third args
    let params = null;
    let fallback = null;
    if (paramsOrFallback && typeof paramsOrFallback === 'object') {
      params = paramsOrFallback;
      fallback = typeof maybeFallback === 'string' ? maybeFallback : null;
    } else if (typeof paramsOrFallback === 'string') {
      fallback = paramsOrFallback;
    }

    const interpolate = (str) => {
      if (!params || typeof str !== 'string') return str;
      return str.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) =>
        params[k] !== undefined && params[k] !== null ? String(params[k]) : `{{${k}}}`,
      );
    };

    // Validate key
    if (!key || typeof key !== 'string') {
      if (DEV_MODE) console.warn('[i18n] Invalid translation key:', key);
      return fallback || key || '';
    }

    const keys = key.split('.');

    // Try current language
    let value = translations[currentLanguage];
    let found = true;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        found = false;
        break;
      }
    }

    // If found and is a string, return it
    if (found && typeof value === 'string') {
      return interpolate(value);
    }

    // Non-string values (arrays/objects) — return as-is when found.
    // Used by content surfaces that bind structured locale data
    // (e.g., a list of tips, or a {type, description, tags} card).
    if (found && (Array.isArray(value) || (value && typeof value === 'object'))) {
      return value;
    }

    // Fallback to English
    let englishValue = translations.en;
    let englishFound = true;

    for (const k of keys) {
      if (englishValue && typeof englishValue === 'object' && k in englishValue) {
        englishValue = englishValue[k];
      } else {
        englishFound = false;
        break;
      }
    }

    // If English translation exists, return it
    if (englishFound && typeof englishValue === 'string') {
      // Warn in dev mode about missing translation
      if (DEV_MODE && currentLanguage !== 'en' && !missingKeysCache.has(`${currentLanguage}:${key}`)) {
        console.warn(`[i18n] Missing translation for "${key}" in "${currentLanguage}", using English fallback`);
        missingKeysCache.add(`${currentLanguage}:${key}`);
      }
      return interpolate(englishValue);
    }

    // English non-string fallback (arrays/objects)
    if (englishFound && (Array.isArray(englishValue) || (englishValue && typeof englishValue === 'object'))) {
      if (DEV_MODE && currentLanguage !== 'en' && !missingKeysCache.has(`${currentLanguage}:${key}`)) {
        console.warn(`[i18n] Missing translation for "${key}" in "${currentLanguage}", using English fallback`);
        missingKeysCache.add(`${currentLanguage}:${key}`);
      }
      return englishValue;
    }

    // Last resort: return provided fallback or empty string
    if (DEV_MODE && !missingKeysCache.has(`en:${key}`)) {
      console.error(`[i18n] Translation key "${key}" not found in any language file`);
      missingKeysCache.add(`en:${key}`);
    }

    // Never return raw key to user - return fallback or empty string
    return interpolate(fallback) || '';
  };

  const getCurrentLanguageInfo = () => {
    return languages.find(l => l.code === currentLanguage) || languages[0];
  };

  // Helper to check if translation exists
  const hasTranslation = (key) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return false;
      }
    }
    
    return typeof value === 'string';
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    languages,
    translations: translations[currentLanguage],
    isChanging,
    getCurrentLanguageInfo,
    isRTL: getCurrentLanguageInfo().rtl,
    hasTranslation,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export default LanguageContext;
